import React from "react";
import Card from "components/Card/Card";
import { useForm, SubmitHandler } from "react-hook-form";
import Page from "components/Page/Page";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { createOrganisation } from "services/clinics.service";
import { readSelfProfile } from "services/profile.service";

type FormValues = {
  orgName: string;
  currency: string;
};

const CURRENCY_MAP: Record<string, number> = {
  euro: 978,
  dollar: 840,
  pound: 826,
};

const NewClinic = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: { orgName: "", currency: "euro" },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const monetaryCurrency = CURRENCY_MAP[data.currency] ?? 978;
      const { data: profile, error: profileError } = await readSelfProfile(token);
      if (profileError || !profile?.userId) {
        setSubmitError(profileError?.message ?? "Could not resolve current user");
        return;
      }
      const { data: res, error } = await createOrganisation(token, {
        orgName: data.orgName,
        monetaryCurrency,
        creatorId: profile.userId,
      });
      if (error) {
        setSubmitError(error.message);
        return;
      }
      const newId =
        (res as { newRecordID?: number; NewRecordID?: number })?.newRecordID ??
        (res as { newRecordID?: number; NewRecordID?: number })?.NewRecordID;
      if (newId != null && Number.isFinite(Number(newId))) {
        navigate(`/app/clinics/${newId}`);
      } else {
        navigate("/app/clinics");
      }
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card
        title="Add New Clinic"
        subTitle="Organisation name and currency (see Swagger ENUM_SYS_CurrencyOption)"
        className="mt-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="p-10">
          <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-10">
            <Input
              {...register("orgName", { required: true })}
              label="Clinic / organisation name"
              placeholder="e.g. Northside Veterinary Clinic"
            />

            <Select
              {...register("currency")}
              label="Currency"
              placeholder="Currency"
              options={[
                { title: "Euro", value: "euro" },
                { title: "Dollar", value: "dollar" },
                { title: "Pound", value: "pound" },
              ]}
            />
          </div>

          {submitError && (
            <div className="mb-4 text-sm text-red-500">{submitError}</div>
          )}

          <Button color="Primary" type="submit" disabled={submitting}>
            <span>{submitting ? "Creating…" : "Create Clinic"}</span>
          </Button>
        </form>
      </Card>
    </Page>
  );
};

export default NewClinic;
